import { FC } from 'react';
import { Button, Input } from '@/components/ui';
import { useSummarizer } from '../hooks/useSummarizer';
import { hasItems } from '@/utils/array';

export const SummarizerTab: FC = () => {
    const {
        websiteUrl,
        setWebsiteUrl,
        summary,
        isSummarizing,
        summaryError,
        handleSummarize,
        safeParseSummaryText
    } = useSummarizer();

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Website Summarizer</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Enter your email or website URL to generate a structured summary using AI.
                </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website URL <span className="text-red-500">*</span>
                    </label>
                    <Input
                        type="text"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://example.com"
                    />
                    {summaryError && (
                        <p className="text-xs text-red-600 mt-2">{summaryError}</p>
                    )}
                </div>

                <Button
                    onClick={handleSummarize}
                    variant="primary"
                    size="md"
                    disabled={isSummarizing}
                    isLoading={isSummarizing}
                >
                    Generate Summary
                </Button>

                {isSummarizing && (
                    <div className="mt-6 text-center">
                        <div className="inline-block w-48 h-1 bg-gray-700/20 rounded-full overflow-hidden relative">
                            <div className="absolute top-0 left-0 h-full w-1/3 bg-yellow-400 animate-[loading_1.5s_infinite]" />
                        </div>
                        <p className="mt-3 text-sm text-gray-500">Analyzing content...</p>

                        <style>
                            {`@keyframes loading {
              0% { transform: translateX(-100%); }
              50% { transform: translateX(150%); }
              100% { transform: translateX(200%); }
            }`}
                        </style>
                    </div>
                )}

                {summary && (
                    <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-6 animate-fadeIn">
                        <h3 className="text-lg font-semibold text-gray-900">Result</h3>

                        {/* Snapshot */}
                        <div className="rounded-lg border border-gray-200 bg-white p-4">
                            <div className="flex items-start gap-4">
                                {/* Logo */}
                                {summary.company_setup?.company_profile?.logo_url ? (
                                    <img
                                        src={summary.company_setup.company_profile.logo_url}
                                        alt={`${summary.company_name ?? 'Company'} logo`}
                                        className="h-12 w-12 rounded-md border object-contain"
                                    />
                                ) : (
                                    <div className="h-12 w-12 rounded-md border flex items-center justify-center text-gray-400">
                                        <span className="text-[10px]">No Logo</span>
                                    </div>
                                )}

                                <div className="flex-1">
                                    <div className="text-base font-semibold text-gray-900">
                                        {summary.company_name ?? 'Company'}
                                    </div>

                                    <div className="mt-1 text-sm text-gray-700">
                                        {summary.website ? (
                                            <div>
                                                <span className="font-medium text-gray-700">Website: </span>
                                                <a
                                                    href={summary.website}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-blue-600 hover:underline break-all"
                                                >
                                                    {summary.website}
                                                </a>
                                            </div>
                                        ) : null}
                                        {summary.industry && (
                                            <div>
                                                <span className="font-medium text-gray-700">Industry: </span>
                                                {summary.industry}
                                            </div>
                                        )}
                                        {summary.product_type && (
                                            <div>
                                                <span className="font-medium text-gray-700">Type: </span>
                                                {summary.product_type}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Top-level descriptions */}
                        {(summary.description || summary.company_description || summary.service_description) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {summary.description && (
                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                        <p className="text-sm font-medium text-gray-600 mb-1">Description</p>
                                        <p className="text-gray-900">{summary.description}</p>
                                    </div>
                                )}
                                {summary.company_description && (
                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                        <p className="text-sm font-medium text-gray-600 mb-1">Company Description</p>
                                        <p className="text-gray-900">{summary.company_description}</p>
                                    </div>
                                )}
                                {summary.service_description && (
                                    <div className="bg-white rounded-lg border border-gray-200 p-4 md:col-span-2">
                                        <p className="text-sm font-medium text-gray-600 mb-1">Service Description</p>
                                        <p className="text-gray-900">{summary.service_description}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Parsed summary text */}
                        {safeParseSummaryText(summary.summary) && (
                            <div className="bg-white rounded-lg border border-gray-200 p-4">
                                <p className="text-sm font-medium text-gray-600 mb-1">AI Summary</p>
                                <p className="text-gray-900">{safeParseSummaryText(summary.summary)}</p>
                            </div>
                        )}

                        {/* Company Setup */}
                        {summary.company_setup && (
                            <div className="space-y-6">
                                {/* Product / Service */}
                                {(summary.company_setup.product_service?.company_description ||
                                    summary.company_setup.product_service?.service_description ||
                                    summary.company_setup.product_service?.type ||
                                    summary.company_setup.product_service?.industry) && (
                                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                                            <p className="text-sm font-medium text-gray-600 mb-3">Product / Service</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-900">
                                                {summary.company_setup.product_service?.type && (
                                                    <div>
                                                        <span className="text-sm text-gray-600">Type</span>
                                                        <div>{summary.company_setup.product_service.type}</div>
                                                    </div>
                                                )}
                                                {summary.company_setup.product_service?.industry && (
                                                    <div>
                                                        <span className="text-sm text-gray-600">Industry</span>
                                                        <div>{summary.company_setup.product_service.industry}</div>
                                                    </div>
                                                )}
                                                {summary.company_setup.product_service?.company_description && (
                                                    <div className="md:col-span-2">
                                                        <span className="text-sm text-gray-600">Company Description</span>
                                                        <div>{summary.company_setup.product_service.company_description}</div>
                                                    </div>
                                                )}
                                                {summary.company_setup.product_service?.service_description && (
                                                    <div className="md:col-span-2">
                                                        <span className="text-sm text-gray-600">Service Description</span>
                                                        <div>{summary.company_setup.product_service.service_description}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                {/* Company Profile */}
                                {(summary.company_setup.company_profile?.brand_guidelines ||
                                    hasItems(summary.company_setup.company_profile?.brand_colors) ||
                                    hasItems(summary.company_setup.company_profile?.key_people)) && (
                                        <div className="bg-white rounded-lg border border-gray-200 p-4">
                                            <p className="text-sm font-medium text-gray-600 mb-3">Company Profile</p>

                                            {summary.company_setup.company_profile?.brand_guidelines && (
                                                <div className="mb-3">
                                                    <span className="text-sm text-gray-600 block">Brand Guidelines</span>
                                                    <p className="text-gray-900">{summary.company_setup.company_profile.brand_guidelines}</p>
                                                </div>
                                            )}

                                            {hasItems(summary.company_setup.company_profile?.brand_colors) && (
                                                <div className="mb-3">
                                                    <span className="text-sm text-gray-600 block mb-1">Brand Colors</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {summary.company_setup.company_profile!.brand_colors!.map((c, i) => (
                                                            <span
                                                                key={i}
                                                                className="inline-flex items-center gap-2 rounded border border-gray-200 px-2 py-1 text-xs"
                                                            >
                                                                <span className="inline-block h-3 w-3 rounded" style={{ background: c }} />
                                                                {c}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {hasItems(summary.company_setup.company_profile?.key_people) && (
                                                <div>
                                                    <span className="text-sm text-gray-600 block mb-1">Key People</span>
                                                    <ul className="list-disc pl-5 text-gray-900">
                                                        {summary.company_setup.company_profile!.key_people!.map((p, i) => (
                                                            <li key={i}>
                                                                {[p?.name, p?.title].filter(Boolean).join(' — ')}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                {/* Others (Competitors, etc.) */}
                                {hasItems(summary.company_setup.competitors) && (
                                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                                        <p className="text-sm font-medium text-gray-600 mb-1">Competitors</p>
                                        <p className="text-gray-900">
                                            {summary.company_setup.competitors!.join(', ')}
                                        </p>
                                    </div>
                                )}

                                {(hasItems(summary.company_setup.content_types) ||
                                    hasItems(summary.company_setup.sales_targets) ||
                                    summary.company_setup.pitch_strategy ||
                                    summary.company_setup.linkedin_company_page ||
                                    summary.company_setup.calendly_url) && (
                                        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
                                            {hasItems(summary.company_setup.content_types) && (
                                                <div>
                                                    <span className="text-sm text-gray-600 block mb-1">Content Generation</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {summary.company_setup.content_types!.map((t, i) => (
                                                            <span key={i} className="rounded bg-gray-100 text-gray-800 px-2 py-1 text-xs">
                                                                {t}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {hasItems(summary.company_setup.sales_targets) && (
                                                <div>
                                                    <span className="text-sm text-gray-600 block mb-1">Sales Targets</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {summary.company_setup.sales_targets!.map((t, i) => (
                                                            <span key={i} className="rounded bg-gray-100 text-gray-800 px-2 py-1 text-xs">
                                                                {t}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {summary.company_setup.pitch_strategy && (
                                                <div>
                                                    <span className="text-sm text-gray-600 block mb-1">Pitch Strategy</span>
                                                    <p className="text-gray-900">{summary.company_setup.pitch_strategy}</p>
                                                </div>
                                            )}

                                            {summary.company_setup.linkedin_company_page && (
                                                <div>
                                                    <span className="text-sm text-gray-600 block mb-1">LinkedIn Company Page</span>
                                                    <a
                                                        href={summary.company_setup.linkedin_company_page}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-600 hover:underline break-all"
                                                    >
                                                        {summary.company_setup.linkedin_company_page}
                                                    </a>
                                                </div>
                                            )}

                                            {summary.company_setup.calendly_url && (
                                                <div>
                                                    <span className="text-sm text-gray-600 block mb-1">Calendly</span>
                                                    <a
                                                        href={summary.company_setup.calendly_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-blue-600 hover:underline break-all"
                                                    >
                                                        {summary.company_setup.calendly_url}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
